import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { getSvgContent, iconContent } from "./request";
import { getIconRegistry } from "./registry";
import { debounce } from "../../utils/debounce";

/**
 * @slot (default) - not to be used, it's purpose is to prevent icon duplication when
 *                   the web component is cloned.
 */

/**
 * this component is heavily inspired by https://github.com/ionic-team/ionicons
 */
@Component({
    tag: "ux-icon",
    styleUrl: "icon.scss",
    shadow: true
})
export class Icon {
    @Element() iconElement: HTMLUxIconElement;
    /** name of icon */
    @Prop({ reflect: true }) readonly name: string = "";
    /** contains the full svg */
    @State() private svgContent: string = null;
    /** custom icon size */
    @State() iconDimension: number;

    /** array with the supported values for the fill prop, can be expanded if needed */
    private supportedFills: string[] = ["primary", "white", "black"];
    /** property to change the color of the icon, supported colors: green, white, black */
    @Prop() readonly fill: string = "";
    /** if set, the filled version (if available) will be selected */
    @Prop() readonly filled: boolean = false;
    /** the size of the icon on mobile viewports */
    @Prop() readonly sizeS: number | null = null;
    /** the size of the icon on tablet viewports */
    @Prop() readonly sizeM: number | null = null;
    /** the size of the icon on desktop viewports */
    @Prop() readonly sizeL: number | null = null;
    /** weight of the icon, either "light", "normal", or "bold", defaults to normal */
    @Prop() readonly weight: string = null;

    private tabletMediaWatcher = window.matchMedia("(min-width: 768px)");
    private desktopMediaWatcher = window.matchMedia("(min-width: 1024px)");

    private hasCustomDimensions = () => {
        return this.sizeS !== null || this.sizeM !== null || this.sizeL !== null;
    };

    private updateIconDimension = () => {
        if (!this.hasCustomDimensions()) {
            return;
        }
        if (this.desktopMediaWatcher.matches) {
            this.iconDimension = this.sizeL || this.sizeM || this.sizeS;
        } else if (this.tabletMediaWatcher.matches) {
            this.iconDimension = this.sizeM || this.sizeS || this.sizeL;
        } else {
            this.iconDimension = this.sizeS || this.sizeM || this.sizeL;
        }
    };

    componentWillLoad() {
        this.updateIconDimension();
        this.loadIcon();
    }

    connectedCallback() {
        this.tabletMediaWatcher.addEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.addEventListener("change", this.updateIconDimension);
    }

    disconnectedCallback() {
        this.tabletMediaWatcher.removeEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.removeEventListener("change", this.updateIconDimension);
    }

    /** Reload asset – called from the `IconRegistry` when the `assetsPath` has changed */
    @Method()
    async reload(): Promise<void> {
        this.loadIconDebounced();
    }

    @Watch("name")
    @Watch("filled")
    @Watch("weight")
    updateIcon() {
        this.loadIconDebounced();
    }

    private loadIcon = (): void => {
        if (this.name === "") {
            this.svgContent = null;
            return;
        }
        const url = getIconRegistry().resolveAssetUrl(this);
        if (url === null) {
            this.svgContent = null;
            if (loggedWarnings.includes(this.name)) {
                return;
            }
            /* eslint-disable-next-line no-console */
            console.warn(`Icon ${this.name} is not available in ux-icon.`);
            loggedWarnings.push(this.name);
            return;
        }
        if (iconContent.has(url)) {
            // sync if it's already loaded
            this.svgContent = iconContent.get(url);
        } else {
            // async if it hasn't been loaded
            getSvgContent(url)
                .then(() => (this.svgContent = iconContent.get(url)))
                .catch(() => {
                    /* eslint-disable-next-line no-console */
                    console.warn(`Icon ${this.name} could not be loaded from ${url}`);
                });
        }
    };

    private loadIconDebounced = debounce(this.loadIcon, 100);

    render() {
        if (this.name === "") {
            return null;
        }
        const customCss = this.hasCustomDimensions() ? { fontSize: `${this.iconDimension}px` } : null;
        return (
            <Host
                class={{
                    ["ux-icon"]: true,
                    [`ux-icon--${this.fill}`]: this.supportedFills.includes(this.fill)
                }}
                style={customCss}
            >
                <div class="ux-icon__inner" innerHTML={this.svgContent} />
                <div class="ux-icon__slot">
                    <slot />
                </div>
            </Host>
        );
    }
}

// this array holds logged warnings about unknown icons to prevent duplicate warnings
const loggedWarnings = [];
